import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import useGoBack from '../hooks/useGoBack'
import {
  Form,
  Select,
  DatePicker,
  Button,
  Table,
  InputNumber,
  Input,
  Card,
  message,
  Spin,
  Space,
  Breadcrumb,
  Row,
  Col,
  Typography,
  Tag
} from 'antd'
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  FilterOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { getInventoryProducts, getInventoryCategories } from '../api/inventory'
import api from '../utils/api'
import { useMediaQuery } from 'react-responsive'
import Hint from '../components/common/Hint'

const { Option } = Select
const { Title, Text } = Typography

// Premium color palette
const colors = {
  primary: '#2C3E50',
  secondary: '#5D7A9B',
  accent: '#8B9DC3',
  text: {
    primary: '#1A1A1A',
    secondary: '#6B7280',
    tertiary: '#9CA3AF'
  },
  background: {
    primary: '#FFFFFF',
    secondary: '#FAFBFC',
    tertiary: '#F5F7FA'
  },
  border: '#E5E7EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444'
}

const spacing = {
  xs: '8px',
  sm: '16px',
  md: '24px',
  lg: '32px',
  xl: '48px'
}

const PageContainer = styled.div`
  margin: 0 auto;
  padding: ${spacing.lg} ${spacing.md};
  background: ${colors.background.secondary};
  min-height: 100vh;

  @media (max-width: 820px) {
    padding: ${spacing.md} ${spacing.sm};
  }
`

const HeaderSection = styled.div`
  margin-bottom: ${spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.sm};
`

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 300;
  color: ${colors.text.primary};
  margin: 0;
  letter-spacing: -0.5px;

  @media (max-width: 820px) {
    font-size: 24px;
  }
`

const ContentCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: ${spacing.lg};

  .ant-card-head {
    border-bottom: 1px solid ${colors.border};
    padding: ${spacing.md} ${spacing.lg};

    .ant-card-head-title {
      font-size: 18px;
      font-weight: 500;
      color: ${colors.text.primary};
    }
  }

  .ant-card-body {
    padding: ${spacing.lg};

    @media (max-width: 820px) {
      padding: ${spacing.md};
    }
  }
`

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    font-weight: 500;
    color: ${colors.text.primary};
  }
`

const ProductTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: ${colors.background.secondary};
    color: ${colors.text.primary};
    font-weight: 500;
    border-bottom: 1px solid ${colors.border};
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${colors.border};
  }

  .ant-table-tbody > tr:hover > td {
    background: ${colors.background.tertiary};
  }

  /* 手機版表格：避免固定欄寬撐破容器，改為橫向捲動 */
  @media (max-width: 820px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px 6px;
    }
  }
`

// 嵌入模式（Modal 內）使用：在手機版收緊整體間距，
// 避免每張卡片之間留太多空白、以及 form item 上下距離過寬。
const EmbeddedFormWrapper = styled.div`
  @media (max-width: 820px) {
    .ant-card {
      margin-bottom: 12px;
      border-radius: 8px;
    }
    .ant-card-head {
      padding: 8px 12px;
      min-height: 40px;
    }
    .ant-card-head-title {
      padding: 8px 0;
      font-size: 15px;
    }
    .ant-card-extra {
      padding: 8px 0;
    }
    .ant-card-body {
      padding: 12px !important;
    }
    .ant-form-item {
      margin-bottom: 10px;
    }
    .ant-form-item-label {
      padding-bottom: 2px;
    }
    .ant-row {
      row-gap: 8px !important;
    }
  }
`

// 手機版商品卡內的 Select：允許選定後的商品名稱換行完整顯示，
// 不被預設的單行省略號截斷（antd Select 預設 white-space: nowrap）。
const MobileProductSelectWrapper = styled.div`
  flex: 1;
  min-width: 0;

  .ant-select-selector {
    height: auto !important;
    min-height: 32px;
    padding-top: 4px !important;
    padding-bottom: 4px !important;
  }
  .ant-select-selection-item {
    white-space: normal !important;
    word-break: break-word;
    line-height: 1.4;
    padding-right: 18px;
  }
  .ant-select-selection-search-input {
    height: auto !important;
  }
`

const SummaryCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-top: ${spacing.lg};

  .ant-card-body {
    padding: ${spacing.lg};
  }
`

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.sm} 0;
  border-bottom: 1px solid ${colors.border};

  &:last-child {
    border-bottom: none;
    font-weight: 600;
    font-size: 18px;
    padding-top: ${spacing.md};
    margin-top: ${spacing.sm};
  }
`

const SummaryLabel = styled.span`
  color: ${colors.text.secondary};
  font-size: 14px;
`

const SummaryValue = styled.span`
  color: ${colors.text.primary};
  font-weight: 500;
  font-size: 16px;
`

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0'
  const numeric = Number(value) || 0
  return `$${numeric.toLocaleString()}`
}

/** 與「容量／重量」直接對齊的計量單位：庫存數字 = ml、g、kg */
const AMOUNT_BASED_UNITS = new Set(['ml', 'g', 'kg'])

const InventoryPurchasePage = ({
  // 嵌入模式：在 Modal 中使用時，省略整頁外殼（PageContainer / Breadcrumb / PageTitle / 返回列表）
  embedded = false,
  // 嵌入模式下用 prop 傳預選商品 ID（取代 location.state）
  preselectedProductId: preselectedProductIdProp = null,
  // 提交成功後的回呼（嵌入模式下取代 navigate('/inventory/products')）
  onSuccess,
  // 取消／關閉 Modal 的回呼（嵌入模式下取代 navigate）
  onClose
} = {}) => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const goBack = useGoBack('/inventory/products')
  const location = useLocation()
  const { user } = useAuth()
  const { hasFeature } = useMembership()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [employees, setEmployees] = useState([])
  const [selectedProducts, setSelectedProducts] = useState([])
  const [fetchingProducts, setFetchingProducts] = useState(false)
  // 商品篩選：分類 + 關鍵字
  const [filterCategoryId, setFilterCategoryId] = useState('all')
  const [filterKeyword, setFilterKeyword] = useState('')

  // 使用 ref 來防止無限循環
  const loadingRef = useRef(false)
  const initializedRef = useRef(false)
  const preselectAppliedRef = useRef(false)

  // 從商品管理頁帶過來的預選商品 ID
  // 嵌入模式優先使用 prop，否則退回 location.state
  const preselectedProductId =
    preselectedProductIdProp || location.state?.preselectedProductId || null

  // 獲取商品列表
  const fetchProducts = useCallback(async () => {
    if (!user?.businessId || !hasFeature('inventory')) return
    if (loadingRef.current) return // 防止重複調用
    loadingRef.current = true
    setFetchingProducts(true)
    try {
      const response = await getInventoryProducts({
        page: 1,
        pageSize: 1000,
        status: 'active' // 只獲取啟用的商品
      })
      const productsData =
        response.data?.data?.items || response.data?.items || []
      setProducts(productsData)
    } catch (error) {
      console.error('獲取商品列表失敗:', error)
      //   message.error('獲取商品列表失敗')
      setProducts([])
    } finally {
      setFetchingProducts(false)
      loadingRef.current = false
    }
  }, [user?.businessId]) // 移除 hasFeature 依賴，在函數內部檢查

  // 獲取商品分類
  const fetchCategories = useCallback(async () => {
    if (!user?.businessId || !hasFeature('inventory')) return
    try {
      const response = await getInventoryCategories()
      let categoriesData = []
      if (Array.isArray(response.data?.data)) {
        categoriesData = response.data.data
      } else if (Array.isArray(response.data)) {
        categoriesData = response.data
      } else if (
        response.data &&
        typeof response.data === 'object' &&
        response.data.code === 200
      ) {
        categoriesData = Array.isArray(response.data.data)
          ? response.data.data
          : []
      }
      setCategories(categoriesData)
    } catch (error) {
      console.error('獲取分類列表失敗:', error)
      setCategories([])
    }
  }, [user?.businessId])

  // 獲取員工列表
  const fetchEmployees = useCallback(async () => {
    if (!user?.businessId || !hasFeature('inventory')) return
    try {
      const response = await api.get(`/api/employees/${user.businessId}`)
      const employeesData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || []
      setEmployees(employeesData)
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
      //   message.error('獲取員工列表失敗')
      setEmployees([])
    }
  }, [user?.businessId]) // 移除 hasFeature 依賴，在函數內部檢查

  useEffect(() => {
    if (!hasFeature('inventory') || initializedRef.current) return
    initializedRef.current = true
    fetchProducts()
    fetchCategories()
    fetchEmployees()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 只在組件掛載時執行一次

  // 商品載入後，若有預選商品則自動加入清單
  useEffect(() => {
    if (preselectAppliedRef.current) return
    if (!preselectedProductId) return
    if (!products || products.length === 0) return

    const product = products.find((p) => p._id === preselectedProductId)
    if (!product) {
      preselectAppliedRef.current = true
      message.warning('找不到指定商品，請手動選擇')
      return
    }

    const productUnit = product.unit || '件'
    const initialQuantity = 1
    const initialPrice = product.cost || product.suggestedPrice || 0
    setSelectedProducts((prev) => [
      ...prev,
      {
        key: `product_${Date.now()}`,
        productId: product._id,
        productName: product.name || product.title,
        productUnit,
        quantity: initialQuantity,
        unitPrice: initialPrice,
        totalPrice: initialPrice * initialQuantity
      }
    ])
    preselectAppliedRef.current = true
  }, [products, preselectedProductId])

  // 添加商品到進貨列表
  const handleAddProduct = () => {
    const newProduct = {
      key: `product_${Date.now()}`,
      productId: null,
      productName: '',
      productUnit: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0
    }
    setSelectedProducts([...selectedProducts, newProduct])
  }

  // 移除商品
  const handleRemoveProduct = (key) => {
    setSelectedProducts(selectedProducts.filter((item) => item.key !== key))
  }

  // 處理商品選擇
  const handleProductChange = (key, productId) => {
    const product = products.find((p) => p._id === productId)
    if (product) {
      const productUnit = product.unit || '件'
      setSelectedProducts(
        selectedProducts.map((item) =>
          item.key === key
            ? {
                ...item,
                productId: product._id,
                productName: product.name || product.title,
                productUnit,
                unitPrice: product.cost || product.suggestedPrice || 0,
                totalPrice:
                  (product.cost || product.suggestedPrice || 0) *
                  (item.quantity || 1)
              }
            : item
        )
      )
    }
  }

  // 處理數量變化
  const handleQuantityChange = (key, quantity) => {
    setSelectedProducts(
      selectedProducts.map((item) => {
        if (item.key === key) {
          const newQuantity = quantity || 1
          return {
            ...item,
            quantity: newQuantity,
            totalPrice: (item.unitPrice || 0) * newQuantity
          }
        }
        return item
      })
    )
  }

  // 處理單價變化
  const handlePriceChange = (key, price) => {
    setSelectedProducts(
      selectedProducts.map((item) => {
        if (item.key === key) {
          const newPrice = price || 0
          return {
            ...item,
            unitPrice: newPrice,
            totalPrice: newPrice * (item.quantity || 1)
          }
        }
        return item
      })
    )
  }

  // 計算總計
  const totalAmount = selectedProducts.reduce(
    (sum, item) => sum + (item.totalPrice || 0),
    0
  )

  const unitForRow = (record) => {
    if (record.productUnit) return record.productUnit
    if (record.productId) {
      const p = products.find((x) => x._id === record.productId)
      return p?.unit || '件'
    }
    return '件'
  }

  // 取得商品分類 ID（兼容多種資料結構）
  const getProductCategoryId = (product) =>
    product?.categoryId || product?.category?.id || product?.category?._id || null

  // 取得商品分類名稱
  const getProductCategoryName = (product) => {
    if (product?.categoryName) return product.categoryName
    if (product?.category?.name) return product.category.name
    const cid = getProductCategoryId(product)
    if (cid) {
      const cat = categories.find((c) => c._id === cid)
      if (cat?.name) return cat.name
    }
    return ''
  }

  // 依分類與關鍵字過濾商品（給每列 Select 下拉選單使用）
  const getFilteredProducts = (currentProductId) => {
    const keyword = (filterKeyword || '').trim().toLowerCase()
    return products.filter((product) => {
      // 永遠保留目前列已選的商品，避免切換篩選後選項消失
      if (currentProductId && product._id === currentProductId) return true
      const categoryMatch =
        filterCategoryId === 'all' ||
        getProductCategoryId(product) === filterCategoryId
      if (!categoryMatch) return false
      if (!keyword) return true
      const name = (product.name || product.title || '').toLowerCase()
      const manufacturer = (product.manufacturer || '').toLowerCase()
      return name.includes(keyword) || manufacturer.includes(keyword)
    })
  }

  const hasAmountBasedLine = selectedProducts.some((row) =>
    AMOUNT_BASED_UNITS.has(unitForRow(row))
  )

  // 提交進貨
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      if (selectedProducts.length === 0) {
        message.warning('請至少選擇一項商品')
        return
      }

      // 驗證所有商品都已選擇
      const hasEmptyProduct = selectedProducts.some((item) => !item.productId)
      if (hasEmptyProduct) {
        message.warning('請選擇所有商品')
        return
      }

      setLoading(true)

      const requestData = {
        employeeId: values.employeeId,
        employeeName: employees.find((e) => e._id === values.employeeId)?.name,
        paymentMethod: values.paymentMethod, // 'cash' 或 'nonCash'
        stockDate: values.stockDate.format('YYYY-MM-DD'), // 入庫日期
        paymentDate: values.paymentDate.format('YYYY-MM-DD'), // 付款日期
        items: selectedProducts
          .filter((p) => p.productId)
          .map((p) => ({
            productId: p.productId,
            productName: p.productName,
            quantity: Number(p.quantity) || 1,
            unitPrice: Number(p.unitPrice) || 0,
            totalPrice: Number(p.totalPrice) || 0
          })),
        totalAmount: totalAmount,
        note: values.note || ''
      }

      const response = await api.post(
        `/api/store/inventory/purchase`,
        requestData
      )

      if (response.data.success) {
        message.success('進貨記錄建立成功')
        form.resetFields()
        setSelectedProducts([])
        if (embedded) {
          // 嵌入模式：交給父層決定要不要關閉 Modal、刷新清單等
          if (typeof onSuccess === 'function') {
            onSuccess(response.data)
          }
        } else {
          navigate('/inventory/products')
        }
      }
    } catch (error) {
      console.error('提交進貨失敗:', error)
      message.error(
        error.response?.data?.message || error.message || '提交進貨失敗'
      )
    } finally {
      setLoading(false)
    }
  }

  // 表格列定義
  const columns = [
    {
      title: '商品',
      key: 'product',
      width: 280,
      render: (_, record) => {
        const optionsForRow = getFilteredProducts(record.productId)
        return (
          <Select
            placeholder='請選擇商品'
            value={record.productId}
            onChange={(value) => handleProductChange(record.key, value)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp='label'
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={
              products.length === 0
                ? '尚未建立任何商品'
                : '此分類／關鍵字下沒有符合的商品'
            }
          >
            {optionsForRow.map((product) => {
              const productLabel = `${product.name || product.title}${
                product.manufacturer ? ` (${product.manufacturer})` : ''
              }`
              const categoryName = getProductCategoryName(product)
              return (
                <Option
                  key={product._id}
                  value={product._id}
                  label={productLabel}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {productLabel}
                    </span>
                    {categoryName && (
                      <Tag
                        color='blue'
                        style={{ marginRight: 0, flexShrink: 0 }}
                      >
                        {categoryName}
                      </Tag>
                    )}
                  </div>
                </Option>
              )
            })}
          </Select>
        )
      }
    },
    {
      title: '數量',
      key: 'quantity',
      width: 168,
      render: (_, record) => {
        const u = unitForRow(record)
        const amountBased = AMOUNT_BASED_UNITS.has(u)
        return (
          <InputNumber
            min={amountBased ? 0.01 : 1}
            step={amountBased ? 1 : 1}
            precision={amountBased ? 2 : 0}
            addonAfter={record.productId ? u : undefined}
            value={record.quantity}
            onChange={(value) => handleQuantityChange(record.key, value)}
            style={{ width: '100%' }}
          />
        )
      }
    },
    {
      title: '單價',
      key: 'unitPrice',
      width: 150,
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.01}
          precision={2}
          prefix='$'
          value={record.unitPrice}
          onChange={(value) => handlePriceChange(record.key, value)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: '小計',
      key: 'totalPrice',
      width: 150,
      align: 'right',
      render: (_, record) => (
        <span style={{ fontWeight: 500 }}>
          {formatCurrency(record.totalPrice || 0)}
        </span>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Button
          type='text'
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProduct(record.key)}
        />
      )
    }
  ]

  if (!hasFeature('inventory')) {
    const noPermissionContent = (
      <Card>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <p style={{ fontSize: '16px', color: colors.text.secondary }}>
            此功能需要庫存管理權限才能使用
          </p>
        </div>
      </Card>
    )
    return embedded ? (
      noPermissionContent
    ) : (
      <PageContainer>{noPermissionContent}</PageContainer>
    )
  }

  // 取消按鈕的處理：嵌入模式呼叫 onClose，否則退回列表
  const handleCancel = () => {
    if (embedded) {
      if (typeof onClose === 'function') onClose()
    } else {
      navigate('/inventory/products')
    }
  }

  const formContent = (
    <StyledForm form={form} layout='vertical' onFinish={handleSubmit}>
        <ContentCard title='進貨信息'>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name='employeeId'
                label='經手人'
                rules={[{ required: true, message: '請選擇經手人' }]}
              >
                <Select placeholder='請選擇員工' showSearch>
                  {employees.map((employee) => (
                    <Option key={employee._id} value={employee._id}>
                      {employee.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name='paymentMethod'
                label='購買方式'
                rules={[{ required: true, message: '請選擇購買方式' }]}
              >
                <Select placeholder='請選擇購買方式'>
                  <Option value='cash'>現金</Option>
                  <Option value='nonCash'>非現金</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name='stockDate'
                label='入庫日期'
                tooltip='商品實際入庫的日期，用於庫存增加'
                rules={[{ required: true, message: '請選擇入庫日期' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format='YYYY-MM-DD'
                  placeholder='請選擇入庫日期'
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name='paymentDate'
                label='付款日期'
                tooltip='實際付款的日期，用於採購支出記錄'
                rules={[{ required: true, message: '請選擇付款日期' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format='YYYY-MM-DD'
                  placeholder='請選擇付款日期'
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item name='note' label='備註'>
                <Input.TextArea rows={3} placeholder='請輸入備註（可選）' />
              </Form.Item>
            </Col>
          </Row>
        </ContentCard>

        <ContentCard
          title='進貨商品'
          extra={
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={handleAddProduct}
            >
              添加商品
            </Button>
          }
        >
          <Spin spinning={fetchingProducts}>
            <Row
              gutter={[12, 12]}
              align='middle'
              style={{ marginBottom: 16 }}
              wrap
            >
              <Col xs={24} sm={12} md={8}>
                <Select
                  value={filterCategoryId}
                  onChange={(value) => setFilterCategoryId(value)}
                  style={{ width: '100%' }}
                  suffixIcon={<FilterOutlined />}
                  placeholder='依分類篩選商品'
                >
                  <Option value='all'>全部分類</Option>
                  {categories.map((cat) => (
                    <Option key={cat._id} value={cat._id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={10}>
                <Input
                  allowClear
                  placeholder='搜尋商品名稱或廠商'
                  value={filterKeyword}
                  onChange={(e) => setFilterKeyword(e.target.value)}
                  prefix={<FilterOutlined style={{ color: '#bbb' }} />}
                />
              </Col>
              <Col xs={24} md={6}>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  {filterCategoryId === 'all' && !filterKeyword
                    ? `共 ${products.length} 項商品`
                    : `已篩選：${getFilteredProducts(null).length} / ${products.length} 項`}
                </Text>
              </Col>
            </Row>
            {hasAmountBasedLine && selectedProducts.length > 0 && (
              <Hint
                tone='info'
                style={{ marginBottom: 16 }}
                title='ml／g／kg 計帳商品'
              >
                此類商品「數量」= 本次入庫的總毫升或公克數（欄位後綴為單位）；「單價」建議填每 ml／每 g 成本（小計÷數量），與庫存扣帳一致。若習慣「按瓶」進貨，可改商品單位為「瓶」、在容量填 500ml，必要時消耗用小數瓶數。
              </Hint>
            )}
            {selectedProducts.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: colors.text.tertiary
                }}
              >
                <ShoppingCartOutlined
                  style={{ fontSize: '48px', marginBottom: '16px' }}
                />
                <p>請點擊「添加商品」開始進貨</p>
              </div>
            ) : isMobile ? (
              // 手機版改用緊密卡片清單，避免橫向滑動。
              // 每張卡片代表一筆進貨商品，欄位垂直排列、padding 收緊。
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selectedProducts.map((record) => {
                  const u = unitForRow(record)
                  const amountBased = AMOUNT_BASED_UNITS.has(u)
                  const optionsForRow = getFilteredProducts(record.productId)
                  return (
                    <div
                      key={record.key}
                      style={{
                        border: `1px solid ${colors.border}`,
                        borderRadius: 8,
                        padding: 8,
                        background: colors.background.primary
                      }}
                    >
                      {/* 商品 + 刪除 */}
                      <div
                        style={{
                          display: 'flex',
                          gap: 6,
                          alignItems: 'flex-start'
                        }}
                      >
                        <MobileProductSelectWrapper>
                          <Select
                            placeholder='請選擇商品'
                            value={record.productId}
                            onChange={(value) =>
                              handleProductChange(record.key, value)
                            }
                            style={{ width: '100%' }}
                            showSearch
                            size='small'
                            // 選中後 trigger 顯示用 label（純文字，可換行完整顯示）；
                            // 下拉內仍走 children 的 JSX 顯示分類 Tag。
                            optionLabelProp='label'
                            optionFilterProp='label'
                            // 自訂 popup 樣式 className（搭配 index.css 給每列一條底線分隔）
                            popupClassName='mobile-purchase-product-dropdown'
                            filterOption={(input, option) =>
                              (option?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            notFoundContent={
                              products.length === 0
                                ? '尚未建立任何商品'
                                : '此分類／關鍵字下沒有符合的商品'
                            }
                          >
                            {optionsForRow.map((product) => {
                              const productLabel = `${
                                product.name || product.title
                              }${
                                product.manufacturer
                                  ? ` (${product.manufacturer})`
                                  : ''
                              }`
                              const categoryName = getProductCategoryName(
                                product
                              )
                              return (
                                <Option
                                  key={product._id}
                                  value={product._id}
                                  label={productLabel}
                                >
                                  {/* 手機下拉選項：商品名稱與分類 Tag 上下分行，
                                      讓名稱可以完整佔滿一整行不被擠到 */}
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'flex-start',
                                      gap: 4
                                    }}
                                  >
                                    <span
                                      style={{
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.4
                                      }}
                                    >
                                      {productLabel}
                                    </span>
                                    {categoryName && (
                                      <Tag
                                        color='blue'
                                        style={{ marginRight: 0 }}
                                      >
                                        {categoryName}
                                      </Tag>
                                    )}
                                  </div>
                                </Option>
                              )
                            })}
                          </Select>
                        </MobileProductSelectWrapper>
                        <Button
                          type='text'
                          danger
                          size='small'
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveProduct(record.key)}
                          style={{ flexShrink: 0, marginTop: 2 }}
                        />
                      </div>

                      {/* 已選商品的分類 Tag：放在 Select 下方獨立顯示，
                          保留分類資訊但不擠壓商品名稱的可視寬度 */}
                      {record.productId &&
                        (() => {
                          const selected = products.find(
                            (p) => p._id === record.productId
                          )
                          const cat = selected
                            ? getProductCategoryName(selected)
                            : ''
                          if (!cat) return null
                          return (
                            <div style={{ marginTop: 10 }}>
                              <Tag color='blue' style={{ marginRight: 0 }}>
                                {cat}
                              </Tag>
                            </div>
                          )
                        })()}

                      {/* 數量 + 單價 */}
                      <div
                        style={{
                          display: 'flex',
                          gap: 6,
                          marginTop: 6
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 11,
                              color: colors.text.secondary,
                              marginBottom: 2
                            }}
                          >
                            數量
                          </div>
                          <InputNumber
                            size='small'
                            min={amountBased ? 0.01 : 1}
                            step={1}
                            precision={amountBased ? 2 : 0}
                            addonAfter={record.productId ? u : undefined}
                            value={record.quantity}
                            onChange={(value) =>
                              handleQuantityChange(record.key, value)
                            }
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 11,
                              color: colors.text.secondary,
                              marginBottom: 2
                            }}
                          >
                            單價
                          </div>
                          <InputNumber
                            size='small'
                            min={0}
                            step={0.01}
                            precision={2}
                            prefix='$'
                            value={record.unitPrice}
                            onChange={(value) =>
                              handlePriceChange(record.key, value)
                            }
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>

                      {/* 小計 */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: 6,
                          paddingTop: 6,
                          borderTop: `1px dashed ${colors.border}`,
                          fontSize: 13
                        }}
                      >
                        <span style={{ color: colors.text.secondary }}>
                          小計
                        </span>
                        <span style={{ fontWeight: 600 }}>
                          {formatCurrency(record.totalPrice || 0)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <ProductTable
                columns={columns}
                dataSource={selectedProducts}
                rowKey='key'
                pagination={false}
                size='middle'
                scroll={{ x: 'max-content' }}
              />
            )}
          </Spin>
        </ContentCard>

        {selectedProducts.length > 0 && (
          <SummaryCard>
            <SummaryRow>
              <SummaryLabel>進貨總額</SummaryLabel>
              <SummaryValue>{formatCurrency(totalAmount)}</SummaryValue>
            </SummaryRow>
          </SummaryCard>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: spacing.sm,
            marginTop: spacing.lg
          }}
        >
          <Button size='large' onClick={handleCancel}>
            取消
          </Button>
          <Button
            type='primary'
            size='large'
            htmlType='submit'
            loading={loading}
            disabled={selectedProducts.length === 0}
          >
            確認進貨
          </Button>
        </div>
      </StyledForm>
  )

  // 嵌入模式（Modal 內）：不需要整頁外殼，並用 wrapper 收緊手機間距
  if (embedded) {
    return <EmbeddedFormWrapper>{formContent}</EmbeddedFormWrapper>
  }

  return (
    <PageContainer>
      <HeaderSection>
        <div>
          <Breadcrumb
            items={[
              {
                title: (
                  <a onClick={() => navigate('/inventory/products')}>
                    庫存管理
                  </a>
                )
              },
              { title: '進貨管理' }
            ]}
          />
          <PageTitle>進貨管理</PageTitle>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
          返回列表
        </Button>
      </HeaderSection>
      {formContent}
    </PageContainer>
  )
}

export default InventoryPurchasePage
